import { CommonIntegrationInstance } from "./mcpSlice";
import { Card, CardContent, CardHeader } from "@altimateai/lego";
import { Popconfirm, Stack } from "@altimateai/lego";
import { Badge } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { DeleteIcon, EditIcon } from "@/assets/icons";
import useMCPContext from "./useMCPContext";
import { deleteCustomIntegration } from "@/helpers/apis";
import { GET_DATAMATES_CUSTOM_INTEGRATIONS } from "@/pages/query/constants";
import { useQueryClient } from "@tanstack/react-query";
import { TeammatesApplicableFor } from "./constants";
import { isOwner, isAdmin } from "@/services/auth";

interface Props {
  instance: CommonIntegrationInstance;
  onEditOpen: (instance: CommonIntegrationInstance) => void;
}

const CustomIntegration = ({ instance, onEditOpen }: Props) => {
  const { teammate } = useMCPContext();
  const queryClient = useQueryClient();
  const canDelete = isOwner() || isAdmin();

  const handleEditClick = (instance: CommonIntegrationInstance) => {
    onEditOpen(instance);
  };

  const removeIntegration = async (instanceId: string) => {
    await deleteCustomIntegration(instanceId);
    await queryClient.invalidateQueries({ queryKey: [GET_DATAMATES_CUSTOM_INTEGRATIONS] });
    await queryClient.invalidateQueries({
      queryKey: [`FILTER_${TeammatesApplicableFor}`],
    });
  };
  return (
    <div
      key={instance.id}
      className="al-transform al-transition-transform al-duration-100 hover:al-scale-[1.02] active:al-scale-[0.98]"
    >
      <Card
        className={`
          al-w-[300px] al-transition-all al-duration-200 al-bg-card hover:al-bg-muted/10 al-cursor-pointer
          ${teammate?.integrations?.map(i => i.id)?.includes(instance.id!) ? "al-border-primary al-bg-primary/5 hover:al-bg-primary/10" : ""}
        `}
      >
        <CardHeader className="al-p-4 al-pb-2">
          <Stack direction="column" className="al-gap-1">
            <Stack direction="row" className="al-justify-between al-items-center">
              <span className="al-font-medium al-truncate al-text-sm md:al-text-xs lg:al-text-[11px] xl:al-text-[10px]">
                {instance.name}
              </span>
              <Stack align="center">
                <Badge variant="outline" className="al-w-fit al-h-5">
                  {instance.type}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Edit integration"
                  className="al-h-8 al-w-8"
                  onClick={e => {
                    e.stopPropagation();
                    handleEditClick(instance);
                  }}
                >
                  <EditIcon className="al-h-4 al-w-4" />
                </Button>
                {canDelete ? (
                  <Popconfirm
                    title={`Delete ${instance.name}`}
                    description="Are you sure you want to delete this integration? This action cannot be undone."
                    onConfirm={() => removeIntegration(instance.id!)}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete integration"
                      className="al-h-8 al-w-8 hover:al-text-destructive"
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <DeleteIcon className="al-h-4 al-w-4" />
                    </Button>
                  </Popconfirm>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </CardHeader>
        <CardContent className="al-p-4">
          <p className="al-text-sm al-text-muted-foreground al-truncate md:al-text-xs lg:al-text-[11px] xl:al-text-[10px]">
            {instance.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomIntegration;
